import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import { IconButton, LinkButton } from '@onefootprint/ui';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createInlineMarkupRegistry, EditableText } from '@/components/block-primitives';
import { type FieldKeyError, namedFieldKeyError } from '../../store/field-key';
import { type FieldType, isEnumType, isObjectType, type Property } from '../../store/types';
import { newProperty, publishedCriteria } from '../../store/utils';
import type { SchemaPatch } from '../../store/wire';
import FieldKeyErrorMessage from '../field-key-error';
import { SchemaTypeSelect } from '../task/schema';
const INDENT = 16;
const markup = createInlineMarkupRegistry([]);
const CELL = 'min-h-7 py-1 opacity-100!';
// `text-body-2` and `text-snippet-1` are both 14/20, so a mono key and the prose beside it sit on one line.
const PROSE = 'text-body-2 text-primary';
const KEY = 'text-snippet-1 text-primary';
const KEY_INVALID = 'text-snippet-1 text-error';
const HOVER = 'rounded-sm transition-colors hover:bg-hover in-data-force-affordance:bg-hover';

/**
 * Two column sets, measured against the table's own width: the pane loses 420px to the activity panel,
 * and columns that fit the full measure collapse into each other there, so below a threshold the rows
 * stack instead.
 *
 * Both are written out because a container-query variant has to be a literal — Tailwind compiles no
 * rule for a class name it cannot read, so neither the breakpoint nor the width can come from a prop.
 * The widths ride as custom properties, which can.
 */
const VALUE_WIDTH = 240;
/** Narrower than an enum's value column, because a property row spends its width on a third column. */
const KEY_WIDTH = 200;
const ENUM_TABLE = {
  header: 'hidden gap-6 pr-8 @[420px]:flex',
  row: 'relative flex flex-wrap items-start gap-x-6 gap-y-0 pr-8',
  first: 'min-w-0 flex-1 px-2 @[420px]:w-[var(--column)] @[420px]:flex-none',
  prose: 'order-3 w-full min-w-0 px-2 @[420px]:order-none @[420px]:w-auto @[420px]:flex-1',
  type: 'hidden',
  typeHead: 'hidden'
};
const PROPERTY_TABLE = {
  header: 'hidden gap-6 pr-8 @[600px]:flex',
  // Stacked, the whole row carries the nesting indent; in columns it moves to the key alone, so the
  // prose and type columns stay under their headings at every depth.
  row: 'relative ml-[var(--indent)] flex flex-wrap items-start gap-x-6 gap-y-0 pr-8 @[600px]:ml-0',
  first: 'min-w-0 flex-1 px-2 @[600px]:ml-[var(--indent)] @[600px]:w-[var(--column)] @[600px]:flex-none',
  prose: 'order-3 w-full min-w-0 px-2 @[600px]:order-none @[600px]:w-auto @[600px]:flex-1',
  // A chip's box is shorter than a line of text, so the cell drops it half a step onto the same line.
  // The heading keeps no such padding, or it would sit out of line with the other two.
  type: 'order-2 shrink-0 px-2 pt-1 @[600px]:order-none @[600px]:w-40',
  typeHead: 'order-2 shrink-0 px-2 @[600px]:order-none @[600px]:w-40'
};
type TableColumns = typeof PROPERTY_TABLE;

/**
 * The floor matters: the indent eats into the first column at every level, and a few levels deep an
 * unfloored column reaches zero and takes the key it holds with it.
 */
const MIN_COLUMN = 96;
const rowVars = (column: number, indent: number): CSSProperties => ({
  '--column': `${Math.max(MIN_COLUMN, column - indent)}px`,
  '--indent': `${indent}px`
}) as CSSProperties;
export const OutputDetail = t0 => {
  const $ = _c(4);
  const {
    children,
    header
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="px-2"><div className="w-full border-t border-tertiary" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children || $[2] !== header) {
    t2 = <>{t1}<div className="@container flex flex-col gap-2">{header}{children}</div></>;
    $[1] = children;
    $[2] = header;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const ColumnHeader = t0 => {
  const $ = _c(3);
  const {
    className,
    label
  } = t0;
  const t1 = `${className} text-label-2 text-tertiary`;
  let t2;
  if ($[0] !== label || $[1] !== t1) {
    t2 = <p className={t1}>{label}</p>;
    $[0] = label;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const OutputColumns = t0 => {
  const $ = _c(8);
  const {
    left,
    right
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = rowVars(VALUE_WIDTH, 0);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== left) {
    t2 = <ColumnHeader className={ENUM_TABLE.first} label={left} />;
    $[1] = left;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== right) {
    t3 = right && <ColumnHeader className={ENUM_TABLE.prose} label={right} />;
    $[3] = right;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className={ENUM_TABLE.header} style={t1}>{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const PropertyColumns = t0 => {
  const $ = _c(11);
  const {
    name,
    description,
    type
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = rowVars(KEY_WIDTH, 0);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== name) {
    t2 = <ColumnHeader className={PROPERTY_TABLE.first} label={name} />;
    $[1] = name;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== description) {
    t3 = <ColumnHeader className={PROPERTY_TABLE.prose} label={description} />;
    $[3] = description;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== type) {
    t4 = <ColumnHeader className={PROPERTY_TABLE.typeHead} label={type} />;
    $[5] = type;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div className={PROPERTY_TABLE.header} style={t1}>{t2}{t3}{t4}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};

/** A row's delete, at the row's right edge — it removes the row, not either half of it. */
const RowDelete = t0 => {
  const $ = _c(3);
  const {
    label,
    onClick
  } = t0;
  let t1;
  if ($[0] !== label || $[1] !== onClick) {
    t1 = <IconButton aria-label={label} className="absolute right-0 top-0.5 opacity-0 transition-opacity group-hover/row:opacity-70 group-focus-within/row:opacity-70 hover:opacity-100! in-data-force-affordance:opacity-70 [&_svg]:size-3.5" color="error" icon={IcoTrash16} onClick={onClick} size="tiny" />;
    $[0] = label;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
type AddRowProps = {
  label: string;
  /** Says which list this adds to, since every level's link reads "Add property". */
  ariaLabel?: string;
  indent: number;
  hint?: string;
  onClick: () => void;
};
const AddRow = t0 => {
  const $ = _c(12);
  const {
    label,
    ariaLabel,
    indent,
    hint,
    onClick
  } = t0;
  const t1 = 8 + indent;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      paddingLeft: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== ariaLabel || $[3] !== label || $[4] !== onClick) {
    t3 = <LinkButton ariaLabel={ariaLabel} iconComponent={IcoPlusSmall16} iconPosition="left" onClick={onClick} variant="label-3">{label}</LinkButton>;
    $[2] = ariaLabel;
    $[3] = label;
    $[4] = onClick;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== hint) {
    t4 = hint && <span className="text-caption-1 text-error">{hint}</span>;
    $[6] = hint;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className="mt-1 flex items-center gap-2 self-start px-2" style={t2}>{t3}{t4}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};

/**
 * A value is the key its criterion is stored under, so a rename has to move both at once — and it is
 * trimmed here because `publishedCriteria` trims when it decides which values are allowed, so an
 * untrimmed key would be pruned the moment it was written.
 */
const renamed = (values: string[], criteria: Record<string, string>, index: number, raw: string): {
  values: string[];
  criteria: Record<string, string>;
} | null => {
  const next = raw.trim();
  const previous = values[index];
  if (!next || next === previous) return null;
  const carried = {
    ...criteria
  };
  if (carried[previous] !== undefined) {
    delete carried[previous];
    carried[next] = criteria[previous];
  }
  return {
    values: values.map((value, i) => i === index ? next : value),
    criteria: carried
  };
};
type ValueRowsProps = {
  values: string[];
  /** Absent for a caller with nowhere to keep one — an agent input — which drops the second column. */
  criteria?: Record<string, string>;
  /** The enum property these values belong to: nested, they sit in the property table's columns. */
  within?: string;
  indent?: number;
  editable?: boolean;
  onChange: (next: {
    values: string[];
    criteria: Record<string, string>;
  }) => void;
};

/** Values and their criteria, in the two columns an enum reads as: the value, and when to choose it. */
const ValueRows = t0 => {
  const $ = _c(18);
  const {
    values,
    criteria,
    within,
    indent: t1,
    editable: t2,
    onChange
  } = t0;
  const indent = t1 === undefined ? 0 : t1;
  const editable = t2 === undefined ? true : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "agentBuilder.canvas.task.output"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t3);
  const [duplicate, setDuplicate] = useState(null);
  const columns = within ? PROPERTY_TABLE : ENUM_TABLE;
  const width = within ? KEY_WIDTH : VALUE_WIDTH;
  const hover = editable ? HOVER : "";
  let t4;
  if ($[1] !== duplicate || $[2] !== values) {
    t4 = index => {
      const value = values[index];
      if (duplicate?.index === index && duplicate.value === value) {
        return duplicate.attemptedValue;
      }
      const trimmed = value.trim();
      if (trimmed && values.slice(0, index).some(earlier => earlier.trim() === trimmed)) {
        return trimmed;
      }
    };
    $[1] = duplicate;
    $[2] = values;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const duplicateOf = t4;
  let t5;
  if ($[4] !== columns || $[5] !== criteria || $[6] !== duplicateOf || $[7] !== editable || $[8] !== hover || $[9] !== indent || $[10] !== onChange || $[11] !== t || $[12] !== values || $[13] !== width || $[14] !== within) {
    t5 = values.map((value_0, index_0) => <div className={`group/row ${columns.row}`} key={index_0} style={rowVars(width, indent)}><div className={`${columns.first} ${hover}`}><EditableText as="div" block={true} className={`${CELL} ${PROSE}`} editable={editable} label={within ? t(_temp, {
          name: within,
          number: index_0 + 1
        }) : t(_temp2, {
          number: index_0 + 1
        })} onSubmit={next => {
          const attemptedValue = next.trim();
          if (values.some((existing, position) => position !== index_0 && existing.trim() === attemptedValue)) {
            setDuplicate({
              index: index_0,
              value: value_0,
              attemptedValue
            });
            return;
          }
          setDuplicate(null);
          const moved = renamed(values, criteria ?? {}, index_0, next);
          if (moved) {
            onChange(moved);
          }
        }} placeholder={t(_temp3)} registry={markup} value={value_0} />{editable && duplicateOf(index_0) && <p className="text-caption-1 text-error" role="alert">{t(_temp4, {
            value: duplicateOf(index_0)
          })}</p>}</div>{!criteria ? null : value_0.trim() ? <div className={`${columns.prose} ${hover}`}><EditableText as="div" block={true} className={`${CELL} ${PROSE} [&:empty]:text-quaternary`} editable={editable} label={t(_temp5, {
          value: value_0
        })} multiline={true} onSubmit={next_0 => onChange({
          values,
          criteria: {
            ...criteria,
            [value_0]: next_0
          }
        })} optional={true} placeholder={t(_temp6)} registry={markup} value={criteria[value_0] ?? ""} /></div> : editable && <p className={`${columns.prose} ${CELL} text-body-2 text-quaternary`}>{t(_temp7)}</p>}<div className={columns.type} />{editable && <RowDelete label={t(_temp8, {
        value: value_0 || t(_temp9, {
          number: index_0 + 1
        })
      })} onClick={() => {
        setDuplicate(null);
        onChange({
          values: values.filter((_, i) => i !== index_0),
          criteria: criteria ?? {}
        });
      }} />}</div>);
    $[4] = columns;
    $[5] = criteria;
    $[6] = duplicateOf;
    $[7] = editable;
    $[8] = hover;
    $[9] = indent;
    $[10] = onChange;
    $[11] = t;
    $[12] = values;
    $[13] = width;
    $[14] = within;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = <>{t5}</>;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
type EnumCriteriaTableProps = {
  values: string[];
  criteria: Record<string, string>;
  editable?: boolean;
  onChange: (patch: SchemaPatch) => void;
};

/** Criteria are keyed by value, not position: a rename carries its criterion, a reorder cannot scramble them. */
export const EnumCriteriaTable = t0 => {
  const $ = _c(17);
  const {
    values,
    criteria,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.canvas.task.output"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t2);
  let t3;
  if ($[1] !== onChange) {
    t3 = next => onChange({
      enumValues: next.values,
      criteria: publishedCriteria(next.criteria, next.values)
    });
    $[1] = onChange;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const commit = t3;
  let t4;
  if ($[3] !== commit || $[4] !== criteria || $[5] !== editable || $[6] !== values) {
    t4 = <ValueRows criteria={criteria} editable={editable} onChange={commit} values={values} />;
    $[3] = commit;
    $[4] = criteria;
    $[5] = editable;
    $[6] = values;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== commit || $[9] !== criteria || $[10] !== editable || $[11] !== t || $[12] !== values) {
    t5 = editable && <AddRow indent={0} label={t(_temp10)} onClick={() => commit({
      values: [...values, ""],
      criteria
    })} />;
    $[8] = commit;
    $[9] = criteria;
    $[10] = editable;
    $[11] = t;
    $[12] = values;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
type EnumValuesProps = {
  values: string[];
  editable?: boolean;
  onChange: (values: string[]) => void;
};

/** An enum whose values carry no criteria — an agent input, which has nowhere to keep one. */
export const EnumValues = t0 => {
  const $ = _c(15);
  const {
    values,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.canvas.task.output"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t2);
  let t3;
  if ($[1] !== onChange) {
    t3 = next => onChange(next.values);
    $[1] = onChange;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== editable || $[4] !== t3 || $[5] !== values) {
    t4 = <ValueRows editable={editable} onChange={t3} values={values} />;
    $[3] = editable;
    $[4] = t3;
    $[5] = values;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== editable || $[8] !== onChange || $[9] !== t || $[10] !== values) {
    t5 = editable && <AddRow indent={0} label={t(_temp11)} onClick={() => onChange([...values, ""])} />;
    $[7] = editable;
    $[8] = onChange;
    $[9] = t;
    $[10] = values;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
type PropertyRowsProps = {
  properties: Property[];
  onChange: (next: Property[]) => void;
  depth?: number;
  editable?: boolean;
  /** The property these are nested in, so a child's label cannot collide with a parent's. */
  within?: string;
  /**
   * Whether an object must declare properties. An output needs them so Percy knows what to fill in,
   * but an input may be left open: the caller supplies its value, and a vendor owns its shape.
   */
  requireProperties?: boolean;
};
export const PropertyRows = t0 => {
  const $ = _c(25);
  const {
    properties,
    onChange,
    depth: t1,
    editable: t2,
    within,
    requireProperties: t3
  } = t0;
  const depth = t1 === undefined ? 0 : t1;
  const editable = t2 === undefined ? true : t2;
  const requireProperties = t3 === undefined ? true : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      keyPrefix: "agentBuilder.canvas.task.output"
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t4);
  let t5;
  if ($[1] !== onChange || $[2] !== properties) {
    t5 = (index, next) => onChange(properties.map((p, i) => i === index ? next : p));
    $[1] = onChange;
    $[2] = properties;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const replace = t5;
  const indent = depth * INDENT;
  const hover = editable ? HOVER : "";
  let t6;
  if ($[4] !== depth || $[5] !== editable || $[6] !== hover || $[7] !== indent || $[8] !== onChange || $[9] !== properties || $[10] !== replace || $[11] !== requireProperties || $[12] !== t || $[13] !== within) {
    t6 = properties.map((property, index_0) => {
      const position = within ? t(_temp12, {
        within,
        number: index_0 + 1
      }) : t(_temp13, {
        number: index_0 + 1
      });
      const named = property.name || position.toLowerCase();
      const path = within ? `${within}.${named}` : named;
      const keyError = namedFieldKeyError(property, properties);
      const children = property.children ?? [];
      const values = property.enumValues ?? [];
      return <div className="flex flex-col gap-2" key={property.id}><div className="flex flex-col"><div className={`group/row ${PROPERTY_TABLE.row}`} style={rowVars(KEY_WIDTH, indent)}><div className={`${PROPERTY_TABLE.first} ${hover}`}><EditableText as="div" block={true} className={`${CELL} ${keyError ? KEY_INVALID : KEY}`} editable={editable} label={position} onSubmit={name => replace(index_0, {
                ...property,
                name: name.trim()
              })} placeholder={t(_temp14)} registry={markup} value={property.name} /></div><div className={`${PROPERTY_TABLE.prose} ${hover}`}><EditableText as="div" block={true} className={`${CELL} ${PROSE} [&:empty]:text-quaternary`} editable={editable} label={t(_temp15, {
                name: path
              })} multiline={true} onSubmit={description => replace(index_0, {
                ...property,
                description: description || undefined
              })} optional={true} placeholder={t(_temp16)} registry={markup} value={property.description ?? ""} /></div><div className={PROPERTY_TABLE.type}><SchemaTypeSelect ariaLabel={t(_temp17, {
                name: path
              })} canEdit={editable} id={`prop-${property.id}-type`} onChange={patch => replace(index_0, {
                ...property,
                type: (patch.type ?? property.type) as FieldType,
                enumValues: patch.enumValues ?? property.enumValues,
                children: patch.children ?? property.children
              })} schema={{
                type: property.type,
                enumValues: property.enumValues,
                criteria: property.criteria,
                children: property.children
              }} /></div>{editable && <RowDelete label={t(_temp18, {
              name: path
            })} onClick={() => onChange(properties.filter((_, i_0) => i_0 !== index_0))} />}</div>{keyError && <div style={{
            paddingLeft: 8 + indent
          }}><FieldKeyErrorMessage error={keyError} /></div>}</div>{isEnumType(property.type) && <><ValueRows criteria={property.criteria ?? {}} editable={editable} indent={indent + INDENT} onChange={next_0 => replace(index_0, {
            ...property,
            enumValues: next_0.values,
            criteria: publishedCriteria(next_0.criteria, next_0.values)
          })} values={values} within={path} />{editable && <AddRow ariaLabel={t(_temp19, {
            name: path
          })} indent={indent + INDENT} label={t(_temp20)} onClick={() => replace(index_0, {
            ...property,
            enumValues: [...values, ""]
          })} />}</>}{isObjectType(property.type) && <><PropertyRows depth={depth + 1} editable={editable} onChange={next_1 => replace(index_0, {
            ...property,
            children: next_1
          })} properties={children} requireProperties={requireProperties} within={path} />{editable && <AddRow ariaLabel={t(_temp21, {
            name: path
          })} hint={requireProperties && children.length === 0 ? t(_temp22) : undefined} indent={indent + INDENT} label={t(_temp23)} onClick={() => replace(index_0, {
            ...property,
            children: [...children, newProperty(children)]
          })} />}</>}</div>;
    });
    $[4] = depth;
    $[5] = editable;
    $[6] = hover;
    $[7] = indent;
    $[8] = onChange;
    $[9] = properties;
    $[10] = replace;
    $[11] = requireProperties;
    $[12] = t;
    $[13] = within;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== depth || $[16] !== editable || $[17] !== onChange || $[18] !== properties || $[19] !== requireProperties || $[20] !== t) {
    t7 = depth === 0 && editable && <AddRow hint={requireProperties && properties.length === 0 ? t(_temp24) : undefined} indent={0} label={t(_temp25)} onClick={() => onChange([...properties, newProperty(properties)])} />;
    $[15] = depth;
    $[16] = editable;
    $[17] = onChange;
    $[18] = properties;
    $[19] = requireProperties;
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t6 || $[23] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};
function _temp($) {
  return $.nestedValue;
}
function _temp2($_0) {
  return $_0.allowedValue;
}
function _temp3($_1) {
  return $_1.valuePlaceholder;
}
function _temp4($_2) {
  return $_2.duplicateValue;
}
function _temp5($_3) {
  return $_3.whenChosen;
}
function _temp6($_4) {
  return $_4.criteriaPlaceholder;
}
function _temp7($_5) {
  return $_5.nameValueFirst;
}
function _temp8($_6) {
  return $_6.removeValue;
}
function _temp9($_7) {
  return $_7.valueFallback;
}
function _temp10($) {
  return $.addValue;
}
function _temp11($) {
  return $.addValue;
}
function _temp12($) {
  return $.nestedPropertyPosition;
}
function _temp13($_0) {
  return $_0.propertyPosition;
}
function _temp14($_1) {
  return $_1.propertyPlaceholder;
}
function _temp15($_2) {
  return $_2.propertyHolds;
}
function _temp16($_3) {
  return $_3.propertyDescriptionPlaceholder;
}
function _temp17($_4) {
  return $_4.propertyType;
}
function _temp18($_5) {
  return $_5.removeProperty;
}
function _temp19($_6) {
  return $_6.addValueTo;
}
function _temp20($_7) {
  return $_7.addValue;
}
function _temp21($_8) {
  return $_8.addPropertyTo;
}
function _temp22($_9) {
  return $_9.noProperties;
}
function _temp23($_10) {
  return $_10.addProperty;
}
function _temp24($_11) {
  return $_11.noProperties;
}
function _temp25($_12) {
  return $_12.addProperty;
}